/**
 * The Release column (D37; the maintainer's replacement for "Versions (hashes)"): the release a server runs as its
 * commit and publication date, a status against what it is served — latest, pinned, updating, behind, and D40's
 * waiting (with why), held (a backup waiting for its main) and rejected (with the reason) — and each live
 * production with the release its package belongs to. The hashes are the tooltip, never the cell. Shared by the
 * portal's server list (Admin → Sites) and the vendor's ops and rollout views; the words are
 * `@acp/api-contract/releaseText.js`', so `acp nodes list` says the same.
 *
 * `release` absent: an older cloud (or a stack without a release store) sent none — the cell says so and keeps the
 * package hashes it does have in the tooltip.
 */
import type { NodeReleaseWire } from '@acp/api-contract/nodeRelease.js';
import { productionReleaseText, rejectionText, releaseHashes, releaseLabel, releaseStatusText, releaseUpdateText } from '@acp/api-contract/releaseText.js';
import { UNKNOWN_HINT, UNKNOWN_LABEL } from '../lib/openEnum.js';
import { Badge } from './ui/badge.js';

export type NodeReleaseCellProps = {
  readonly release: NodeReleaseWire | undefined;
  /** The packages the server reports holding: the tooltip when there is no `release`. */
  readonly packageHashes: readonly string[];
};

// `unknown`: a status from a newer cloud (D37) — neutral; null: the cloud cannot tell.
// D40: `waiting` (the on-air guard holds the swap), `held` (a backup waiting for its main), `rejected` (a rollback or a refusal).
const STATUS_BADGE = { latest: 'success', pinned: 'secondary', updating: 'next', behind: 'warning', waiting: 'standby', held: 'secondary', rejected: 'destructive', unknown: 'outline' } as const;

/** The status badge: a known status in words, a newer cloud's as Unknown, none as unknown. */
function StatusBadge({ release }: { readonly release: NodeReleaseWire }) {
  if (release.status === null) {
    return (
      <Badge variant="outline" data-testid="release-status">
        unknown
      </Badge>
    );
  }
  return (
    <Badge variant={STATUS_BADGE[release.status]} title={release.status === 'unknown' ? UNKNOWN_HINT : undefined} data-testid="release-status">
      {release.status === 'unknown' ? UNKNOWN_LABEL : releaseStatusText(release)}
    </Badge>
  );
}

export function NodeReleaseCell({ release, packageHashes }: NodeReleaseCellProps) {
  if (release === undefined) {
    return (
      <span className="text-xs text-muted-foreground" title={packageHashes.length === 0 ? undefined : packageHashes.join('\n')} data-testid="node-release" data-status="none">
        not reported
      </span>
    );
  }
  const target = release.target.release;
  const moving = (release.status === 'updating' || release.status === 'behind') && target !== null && target.listHash !== release.running?.listHash;
  // D40: "update waiting: <release> (N productions active)", "held for main: …", "rejected …: <reason>".
  const update = releaseUpdateText(release);
  const rejected = release.rejections ?? [];
  return (
    <div className="grid gap-0.5" title={releaseHashes(release)} data-testid="node-release" data-status={release.status ?? 'none'}>
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-xs" data-testid="release-running">
          {release.running === null ? '–' : releaseLabel(release.running)}
        </span>
        <StatusBadge release={release} />
      </div>
      {moving ? (
        <div className="text-xs text-muted-foreground" data-testid="release-target">
          → {releaseLabel(target)}
        </div>
      ) : null}
      {update === null ? null : (
        <div className="text-xs text-muted-foreground" data-testid="release-update" title={rejected.length === 0 ? undefined : rejected.map(rejectionText).join('\n')}>
          {update}
        </div>
      )}
      {release.productions.map((p) => (
        <div key={p.productionId} className="text-xs text-muted-foreground" data-testid="release-production">
          ▶ {productionReleaseText(p)}
        </div>
      ))}
    </div>
  );
}
